import { ArrowRight, Clock3, Link2, Smartphone, UsersRound } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { BuildingStage } from "../components/BuildingStage";
import { ExperienceCover } from "../components/ExperienceCover";
import { useCatalog } from "../hooks/useCatalog";

const formatLabels = { mystery: "Misterio", connection: "Conexión", party: "Fiesta" } as const;

export function LandingPage() {
  const { items } = useCatalog();
  const navigate = useNavigate();
  const [code, setCode] = useState("");

  const goToRoom = (event: React.FormEvent) => {
    event.preventDefault();
    if (code.trim()) navigate(`/unir?code=${encodeURIComponent(code.trim().toUpperCase())}`);
  };

  return (
    <>
      <section className="landing-hero">
        <div className="hero-copy">
          <h1>Esta noche ya tiene plan.</h1>
          <p className="hero-lede">Elijan qué quieren sentir. Abran un enlace. Cada teléfono recibe una parte distinta de una experiencia que sólo funciona juntos.</p>
          <div className="hero-actions">
            <Link className="button button-citron button-large" to="/catalogo">
              Elegir una experiencia <ArrowRight aria-hidden="true" />
            </Link>
            <span className="action-note">Sin instalar nada · desde $79 MXN por grupo</span>
          </div>
          <form className="join-strip" onSubmit={goToRoom}>
            <label htmlFor="home-code">¿Ya te invitaron?</label>
            <div>
              <input
                id="home-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="CÓDIGO DE SALA"
                maxLength={8}
                autoComplete="off"
              />
              <button type="submit" aria-label="Abrir sala con el código">
                <ArrowRight aria-hidden="true" />
              </button>
            </div>
          </form>
        </div>
        <BuildingStage />
      </section>

      <section className="promise-band" aria-label="Cómo funciona">
        <div><Clock3 aria-hidden="true" /><strong>Empiecen en minutos</strong><span>Compra, comparte y entren.</span></div>
        <div><Smartphone aria-hidden="true" /><strong>Uno o varios teléfonos</strong><span>La sala se adapta al plan.</span></div>
        <div><UsersRound aria-hidden="true" /><strong>Hecho para mirarse</strong><span>La pantalla guía; ustedes juegan.</span></div>
      </section>

      <section className="landing-catalog">
        <div className="section-intro">
          <h2>¿Qué quieren sentir hoy?</h2>
          <p>Cuatro formas de empezar. El catálogo completo abre {items.length} noches con principio, sorpresa y un cierre claro.</p>
        </div>
        <div className="experience-mosaic">
          {items.slice(0, 4).map((item, index) => (
            <Link
              key={item.id}
              className={`experience-tile tile-${index + 1}`}
              to={`/experiencias/${item.slug}`}
              onClick={() => api.track("product_viewed", crypto.randomUUID(), { product_id: item.id })}
            >
              <ExperienceCover item={item} priority={index === 0} />
              <div className="tile-copy">
                <h3>{item.title}</h3>
                <p>{item.tagline}</p>
                <div className="tile-meta">
                  <span>{formatLabels[item.format]}</span>
                  <span>{item.estimatedMinutes} min</span>
                  <span>{item.minPlayers === item.maxPlayers ? item.minPlayers : `${item.minPlayers}–${item.maxPlayers}`} personas</span>
                </div>
              </div>
              <span className="tile-price">${item.priceMxn}</span>
            </Link>
          ))}
        </div>
        <Link className="text-link" to="/catalogo">Ver el catálogo completo <ArrowRight aria-hidden="true" /></Link>
      </section>

      <section className="mechanism-story">
        <div className="mechanism-copy">
          <h2>Dos ventanas. Una sola historia.</h2>
          <p>No pasan la noche leyendo la misma pantalla. Alguien recibe una pista; otra persona, una decisión. La sala espera, conecta ambas piezas y revela lo que provocaron juntos.</p>
          <Link className="button button-light" to="/experiencias/la-habitacion-304">
            Ver cómo empieza el misterio <ArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="mechanism-rail" aria-label="Secuencia de una experiencia">
          <div className="rail-stop is-on"><span>Una sala</span><strong>Enlace compartido</strong><Link2 aria-hidden="true" /></div>
          <div className="rail-stop is-on"><span>Vistas privadas</span><strong>Pistas diferentes</strong><Smartphone aria-hidden="true" /></div>
          <div className="rail-stop is-on"><span>Momento compartido</span><strong>La decisión cambia la escena</strong><UsersRound aria-hidden="true" /></div>
        </div>
      </section>

      <section className="closing-field">
        <div className="closing-windows" aria-hidden="true"><span /><span /><span /><span /><span /></div>
        <div>
          <h2>No necesitan organizar una gran noche.</h2>
          <p>Sólo elegir qué quieren que pase en la próxima hora.</p>
          <Link className="button button-ink button-large" to="/catalogo">Encontrar nuestro plan <ArrowRight aria-hidden="true" /></Link>
        </div>
      </section>
    </>
  );
}
